import React from 'react';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip } from 'recharts';
import type { AnalysisResult } from '../types';

interface ScoreDashboardProps {
  result: AnalysisResult;
}

const PAPER = '#F5F1EA';
const INK = '#15181C';
const INK_4 = '#8A8E93';
const RULE = '#D9D1BD';
const SIGNAL = '#2B6CFF';
const GREEN = '#2E7D4F';
const MID = '#B5842A';
const RED = '#B33A3A';

function scoreColor(s: number) {
  if (s >= 90) return GREEN;
  if (s >= 70) return MID;
  return RED;
}

function scoreLabel(s: number) {
  if (s >= 90) return 'Excellent';
  if (s >= 70) return 'Needs improvement';
  return 'Poor semantics';
}

export const ScoreDashboard: React.FC<ScoreDashboardProps> = ({ result }) => {
  const { score, tagStats, semanticRatio, imagesTotal, imagesWithoutAlt, schemaTypes, metaCoverage, textHtmlRatio } = result;
  const color = scoreColor(score);

  const totalTags = tagStats.semantic + tagStats.generic;
  const semanticPct = totalTags > 0
    ? Math.round((tagStats.semantic / totalTags) * 100)
    : Math.round(semanticRatio * 100);

  const altCoverage = imagesTotal > 0
    ? Math.round(((imagesTotal - imagesWithoutAlt) / imagesTotal) * 100)
    : 100;

  const metaKeys = ['viewport', 'canonical', 'description', 'openGraph', 'twitterCard'] as const;
  const metaPresent = metaKeys.filter((k) => metaCoverage?.[k]).length;
  const metaTotal = metaKeys.length;
  const textPct = Math.round((textHtmlRatio ?? 0) * 100);

  const chartData = [
    { name: 'Semantic', value: tagStats.semantic, color: SIGNAL },
    { name: 'Generic', value: tagStats.generic, color: INK_4 },
  ];

  const RADIUS = 84;
  const CIRC = 2 * Math.PI * RADIUS;
  const offset = CIRC - (CIRC * score) / 100;

  return (
    <div style={{ marginBottom: 32 }}>
      <div className="dashboard-grid">
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
          <span className="eyebrow" style={{ marginBottom: 16 }}>Semantic score</span>
          <div style={{ position: 'relative', width: 200, height: 200 }}>
            <svg width="200" height="200" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="100" cy="100" r={RADIUS} stroke={RULE} strokeWidth="10" fill="none" />
              <circle
                cx="100"
                cy="100"
                r={RADIUS}
                stroke={color}
                strokeWidth="10"
                fill="none"
                strokeDasharray={CIRC}
                strokeDashoffset={offset}
                strokeLinecap="round"
                className="score-ring-fill"
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 56, fontWeight: 500, color: INK, lineHeight: 1 }}>
                {score}
              </span>
              <span className="meta" style={{ marginTop: 4 }}>/ 100</span>
            </div>
          </div>
          <p className="meta" style={{ marginTop: 16, color, fontWeight: 600, letterSpacing: '0.04em' }}>
            {scoreLabel(score)}
          </p>
        </div>

        <div className="card">
          <span className="eyebrow">Structural composition</span>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 24, margin: '12px 0 24px' }}>
            {semanticPct}% semantic
          </h3>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={80}
                  tick={{ fill: INK, fontSize: 12, fontFamily: 'Inter' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{ background: PAPER, border: `1px solid ${RULE}`, borderRadius: 4, fontFamily: 'Inter', fontSize: 13, color: INK }}
                  itemStyle={{ color: INK }}
                />
                <Bar dataKey="value" radius={[0, 2, 2, 0]} barSize={22}>
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="meta" style={{ margin: '12px 0 0' }}>
            {tagStats.semantic} semantic vs {tagStats.generic} generic structural tags
          </p>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span className="eyebrow">At a glance</span>
          <StatRow
            label="Image alt coverage"
            value={imagesTotal > 0 ? `${altCoverage}%` : '—'}
            sub={imagesTotal > 0 ? `${imagesTotal - imagesWithoutAlt} / ${imagesTotal} images` : 'No images'}
            tone={imagesTotal === 0 ? 'neutral' : altCoverage >= 90 ? 'good' : altCoverage >= 60 ? 'warn' : 'bad'}
          />
          <StatRow
            label="Structured data"
            value={schemaTypes.length ? `${schemaTypes.length} type${schemaTypes.length === 1 ? '' : 's'}` : 'None'}
            sub={schemaTypes.length ? schemaTypes.slice(0, 2).join(', ') + (schemaTypes.length > 2 ? '…' : '') : 'No JSON-LD'}
            tone={schemaTypes.length > 0 ? 'good' : 'warn'}
          />
          <StatRow
            label="DOM density"
            value={tagStats.generic > tagStats.semantic * 2 ? 'div-heavy' : 'Balanced'}
            sub={`${totalTags} structural tags`}
            tone={tagStats.generic > tagStats.semantic * 2 ? 'warn' : 'good'}
          />
          <StatRow
            label="Meta coverage"
            value={`${metaPresent} / ${metaTotal}`}
            sub={metaCoverageSummary(metaCoverage)}
            tone={metaPresent === metaTotal ? 'good' : metaPresent >= 3 ? 'warn' : 'bad'}
          />
          <StatRow
            label="Text-to-HTML"
            value={textHtmlRatio != null ? `${textPct}%` : '—'}
            sub={textHtmlRatio != null ? (textPct >= 10 ? 'Healthy ratio' : textPct >= 5 ? 'Markup-heavy' : 'Very thin content') : 'Not estimated'}
            tone={textHtmlRatio == null ? 'neutral' : textPct >= 10 ? 'good' : textPct >= 5 ? 'warn' : 'bad'}
          />
        </div>
      </div>

      <style>{`
        .dashboard-grid {
          display: grid;
          grid-template-columns: 320px 1fr 280px;
          gap: 16px;
        }
        @media (max-width: 980px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

function metaCoverageSummary(m: AnalysisResult['metaCoverage']): string {
  if (!m) return '—';
  const missing: string[] = [];
  if (!m.viewport) missing.push('viewport');
  if (!m.canonical) missing.push('canonical');
  if (!m.description) missing.push('description');
  if (!m.openGraph) missing.push('OG');
  if (!m.twitterCard) missing.push('Twitter');
  if (missing.length === 0) return 'All present';
  if (missing.length <= 2) return `Missing: ${missing.join(', ')}`;
  return `${5 - missing.length}/5 present`;
}

interface StatRowProps {
  label: string;
  value: string;
  sub: string;
  tone: 'good' | 'warn' | 'bad' | 'neutral';
}

const StatRow: React.FC<StatRowProps> = ({ label, value, sub, tone }) => {
  const dotColor = tone === 'good' ? GREEN : tone === 'warn' ? MID : tone === 'bad' ? RED : INK_4;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: 12, borderBottom: `1px solid ${RULE}` }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, marginTop: 7, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="meta" style={{ marginBottom: 2 }}>{label}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, color: INK, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 12, color: INK_4, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
};
